// Fassungen eines Inhalts — der Vertrag zu `content_revisions` im Backend.
//
// Am Backend NACHGEMESSEN, nicht angenommen (energetic-eternity-backend):
//
//   Route      routes/contentRoutes.ts:387  GET  /api/content/:id/versions
//              routes/contentRoutes.ts:394  POST /api/content/:id/versions/:versionId/restore
//   Controller controllers/ContentController.ts:799 / :813
//   Dienst     services/ContentService.ts:1351 → services/RevisionService.getRevisions
//   Schema     prisma/schema.prisma:267 `model ContentRevision`
//
// ⚠️ **Die Liste kommt als OBJEKT, nicht als Array.** Der Controller antwortet mit
// `{ success, data: { revisions: [...], pagination: {...} } }`. Wer `data` für die Liste hält,
// bekommt ein Objekt, `.map` wirft, und die Ansicht bleibt leer.
//
// ⚠️ **Es gibt keine zweite Seite.** `ContentService.getContentVersions` ruft `getRevisions` mit
// fest verdrahtetem `{ page: 1, limit: 50 }` — der Client kann weder blättern noch die Grenze
// heben. Die `pagination` kommt trotzdem mit und trägt `total`; ist `total` größer als die
// gelieferte Menge, ist der Rest über diese Oberfläche NICHT erreichbar. Genau deshalb sagt die
// Ansicht das hin, statt eine Blätter-Leiste anzubieten, die nichts bewirken kann.
//
// ⚠️ **`changedBy` ist eine rohe Benutzer-Kennung oder `'system'`** — keine Relation, kein
// eingebetteter Name (Schema: `changedBy String`, ohne `@relation`). Das Backend löst sie NICHT
// auf. Wer sie ungeprüft anzeigt, schreibt eine cuid in die Tabelle; die Auflösung passiert
// deshalb in der Oberfläche (siehe `components/content/Versionsliste.tsx`).
//
// ⚠️ **Was eine Zeile BEDEUTET, ist die wichtigste Messung dieser Datei.**
// `RevisionService.createRevision` wird VOR dem Schreiben gerufen und bekommt die Zeile, wie sie
// bis dahin war (`ContentService.updateContent`: `const existing = …` → `createRevision(id,
// existing, …)`). Eine Fassung hält also den Stand fest, der VOR der jeweiligen Änderung galt —
// `changedAt` ist der Zeitpunkt der Änderung, `changedFields` sind die Felder, die sie angefasst
// hat, und `snapshot` ist der Stand DAVOR. „Wiederherstellen" holt genau diesen Stand zurück.
// Eine Beschriftung wie „Diese Fassung" ohne diesen Zusatz wäre am Bildschirm eine falsche
// Zusage.
import type { Pagination } from './index';

/**
 * Art der Änderung — spiegelt EXAKT das Prisma-Enum `RevisionChangeType`
 * (`prisma/schema.prisma:286`).
 *
 * Bewusst eine Zeichenketten-Union und kein `enum`: Es gibt im CMS keine Stelle, die einen dieser
 * Werte SCHREIBT — die Oberfläche liest sie nur. Ein Enum brächte drei Glieder, von denen keines
 * je namentlich benutzt würde; genau daran kann `knip --include enumMembers` nichts messen (siehe
 * die drei Gegenproben im Kopf von `knip.jsonc`). Die Anzeigekarte unten deckt dieselbe Rolle ab
 * und ist zugleich das, was die Oberfläche wirklich braucht.
 *
 * ⚠️ Am Backend gemessen: `STATUS_CHANGE` steht im Prisma-Enum, wird aber von KEINEM Schreibpfad
 * erzeugt — `grep createRevision` findet acht Aufrufstellen, vier mit `'UPDATE'` und vier mit
 * `'DELETE'` (ContentService, AudioService, VideoService, MindBookService); `'STATUS_CHANGE'`
 * kommt außerhalb der Backend-Tests nicht vor. Der Wert bleibt hier trotzdem stehen: er ist Teil
 * des Vertrags, in der Datenbank darstellbar, und ein Enum, das nur kennt, was gerade geschrieben
 * wird, ist kein Abbild des Vertrags mehr. Dieselbe Begründung wie bei `ARCHIVED`/`REJECTED` in
 * `ContentStatus`.
 */
export type RevisionChangeType = 'UPDATE' | 'STATUS_CHANGE' | 'DELETE';

/**
 * Eine Fassung, so wie `GET /api/content/:id/versions` sie liefert.
 *
 * Die Felder stehen 1:1 in `model ContentRevision` — der Endpunkt reicht die Zeile ohne `select`
 * durch (`RevisionService.getRevisions`: `findMany({ where, orderBy, skip, take })`).
 */
export interface ContentRevision {
  id: string;
  contentId: string;
  /** Aus dem Snapshot abgeleitet (`snapshot.type`), Rückfall `'UNKNOWN'`. */
  contentType: string;
  /** Fortlaufend ab 1, absteigend sortiert geliefert (`orderBy: { version: 'desc' }`). */
  version: number;
  changeType: RevisionChangeType;
  /**
   * Die Felder, die die Änderung angefasst hat — NICHT die Felder dieser Momentaufnahme.
   * Inhalt ist `Object.keys(updateData)`, also die Schlüssel des Anfrage-Körpers; beim
   * automatischen Schnappschuss vor einer Wiederherstellung `['_restored']`.
   */
  changedFields: string[];
  /** Rohe Benutzer-Kennung oder `'system'`. Siehe Warnung im Dateikopf. */
  changedBy: string;
  changedAt: string;
  /** Leer bei gewöhnlichen Änderungen: `createRevision` schreibt `description ?? ''`. */
  description?: string | null;
  /**
   * Der Stand VOR der Änderung, als freies JSON. Diese Runde zeigt ihn NICHT an — ein
   * Feld-Vergleich zweier Fassungen ist bewusst nicht Teil des Umfangs (Strapi liefert seinen
   * Versionsverlauf ebenfalls ohne Vergleich aus). Er steht hier, weil er über die Leitung kommt.
   */
  snapshot?: Record<string, unknown>;
}

/** Die Antwortform des Listen-Endpunkts, nach `normalizePagination` gebracht. */
export interface ContentRevisionList {
  revisions: ContentRevision[];
  pagination: Pagination;
}

/**
 * Anzeigetext und Farbton je Änderungsart — dieselbe Rolle wie `STATUS_ANZEIGE` in `types/index.ts`.
 *
 * ⚠️ Bewusst NICHT über `StatusBadge`: dessen Karte (`STATUS_ANZEIGE`) kennt nur die
 * INHALTS-Status `DRAFT`/`PUBLISHED`/`ARCHIVED`/`REJECTED`. Eine Änderungsart dort einzutragen
 * hieße, zwei verschiedene Begriffe in eine Karte zu legen; ein Inhalt kann gleichzeitig
 * „Entwurf" sein und eine Fassung vom Typ „Gelöscht" haben. Beim ersten gleichnamigen Wert liefen
 * sie ineinander.
 *
 * Schlüssel sind Zeichenketten und nicht `RevisionChangeType`, weil der Wert roh aus der Antwort
 * kommt: ein unbekanntes Glied ergibt `undefined` und die Anzeige zeigt den rohen Wert, statt eine
 * leere Fläche zu rendern. Dasselbe Verhalten wie bei `StatusBadge` — sichtbar falsch schlägt
 * unsichtbar weg.
 */
export const AENDERUNGSART_ANZEIGE: Record<string, { text: string; ton: string }> = {
  UPDATE: { text: 'Bearbeitet', ton: 'badge-primary' },
  STATUS_CHANGE: { text: 'Status geändert', ton: 'badge-warning' },
  DELETE: { text: 'Gelöscht', ton: 'badge-error' },
};

/**
 * Deutsche Namen für die Feldschlüssel, die in `changedFields` vorkommen KÖNNEN.
 *
 * ⚠️ Die Karte ist absichtlich klein und deckt genau das ab, was nachweisbar ist. `changedFields`
 * ist `Object.keys(updateData)` — also die Schlüssel dessen, was ein Aufrufer geschickt hat, und
 * damit prinzipiell offen. Eingetragen sind nur die zwölf Schlüssel, die das CMS selbst schreibt
 * (`ContentManagement.handleFormSubmit`, Objekt `transformedData`) plus die Marke `_restored`, die
 * `RevisionService.restoreRevision` für seinen automatischen Schnappschuss setzt. Alles andere
 * bleibt roh stehen — geraten wird hier nichts.
 */
export const FELD_ANZEIGE: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  type: 'Inhaltstyp',
  slug: 'Adresskürzel',
  category: 'Kategorie',
  tags: 'Schlagwörter',
  locale: 'Sprachraum',
  language: 'Sprache',
  isPremium: 'Premium',
  content: 'Inhalt',
  contentData: 'Inhaltsdaten',
  metadata: 'Zusatzangaben',
  _restored: 'Automatischer Schnappschuss',
};
